import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase } from '../utils/supabase'

interface AdminUser {
  id: string
  email: string
  name: string
  role: 'admin' | 'editor' | 'finance' | 'librarian' | null
}

interface AuthContextValue {
  user: AdminUser | null
  isAuthenticated: boolean
  isAdmin: boolean
  loading: boolean
  authorizationError: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(() => {
    // Hydrate immediately from localStorage — no flash
    try {
      const saved = localStorage.getItem('nwlc_admin_auth')
      if (saved) return JSON.parse(saved) as AdminUser
    } catch {}
    return null
  })
  const [loading, setLoading] = useState(true)
  const [authorizationError, setAuthorizationError] = useState<string | null>(null)

  const applyUser = (u: AdminUser) => {
    setUser(u)
    setAuthorizationError(null)
    localStorage.setItem('nwlc_admin_auth', JSON.stringify(u))
  }

  const resolveSessionUser = async (sessionUser: { id: string; email?: string }) => {
    // Build admin user from session — don't block on user_roles query
    const base: AdminUser = {
      id: sessionUser.id,
      email: sessionUser.email || '',
      name: sessionUser.email?.split('@')[0] || 'NWLC Staff',
      role: 'admin'
    }

    // Try to enrich with role from DB (non-blocking best-effort)
    try {
      const { data } = await Promise.race([
        supabase.from('user_roles').select('role').eq('user_id', sessionUser.id).maybeSingle(),
        new Promise<{ data: null }>((resolve) => setTimeout(() => resolve({ data: null }), 2000))
      ]) as { data: { role: string } | null }

      if (data?.role && ['admin', 'editor', 'finance', 'librarian'].includes(data.role)) {
        base.role = data.role as AdminUser['role']
      }
    } catch {
      // Ignore — use default 'admin' role
    }

    applyUser(base)
  }

  useEffect(() => {
    let mounted = true

    // Safety net — never block UI more than 1.5s
    const timer = setTimeout(() => {
      if (mounted) setLoading(false)
    }, 1500)

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!mounted) return

        if (session?.user) {
          // Session found — resolve user (already hydrated from localStorage if available)
          await resolveSessionUser(session.user)
        } else if (!user) {
          // No session and no localStorage user — not authenticated
          setUser(null)
        }
      } catch (err) {
        console.error('Auth init error:', err)
      } finally {
        if (mounted) {
          clearTimeout(timer)
          setLoading(false)
        }
      }
    }

    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return

      if (event === 'SIGNED_OUT') {
        setUser(null)
        localStorage.removeItem('nwlc_admin_auth')
        setLoading(false)
        return
      }

      if (session?.user) {
        await resolveSessionUser(session.user)
      }

      setLoading(false)
    })

    return () => {
      mounted = false
      clearTimeout(timer)
      subscription.unsubscribe()
    }
  }, [])

  const login = async (email: string, password: string) => {
    // signInWithPassword triggers onAuthStateChange which handles resolveSessionUser
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    // Don't await resolveUser here — onAuthStateChange handles it
    // Just give the state change a moment to propagate
  }

  const logout = async () => {
    localStorage.removeItem('nwlc_admin_auth')
    setUser(null)
    await supabase.auth.signOut()
  }

  const isAuthenticated = !!user
  const isAdmin = user?.role === 'admin' || user?.role === 'editor'

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isAdmin,
      loading,
      authorizationError,
      login,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
